/**
 * Viewport-size classifier for responsive layout decisions.
 *
 * Maps `window.innerWidth` onto four tiers per `lexide-implementation-plan.md`
 * §7.7 (responsive behavior) + `lexide-concept.md` §5.10:
 *
 *   mobile   <  768   → bottom-bar shell, sheet drawers, single-column stacks
 *   tablet   768-1024 → rail visible, sheet/overlay side panel, full-width content
 *   medium   1024-1400 → smaller AC pane, chat/file split 40/60
 *   wide     ≥ 1400   → full four-pane grid as drawn
 *
 * Tailwind's `lg` breakpoint is 1024px (matches the tablet→medium edge);
 * `md` is 768px (matches the mobile→tablet edge); aligning with Tailwind
 * keeps utility-class breakpoints consistent across the codebase.
 *
 * **Backwards-compat:** the pre-§7.7 type was `"compact" | "medium" | "wide"`
 * with `compact` covering everything under 1024px. The new `mobile` + `tablet`
 * split that band; existing call sites that conceptually wanted "narrow
 * desktop / tablet" should use the `isCompactViewport(size)` helper which
 * returns true for mobile OR tablet — preserving the historic semantics.
 */

import { useEffect, useState } from "react";

export type ViewportSize = "mobile" | "tablet" | "medium" | "wide";

const MOBILE_MAX = 768;
const TABLET_MAX = 1024;
const MEDIUM_MAX = 1400;

export function classifyViewport(width: number): ViewportSize {
  if (width < MOBILE_MAX) return "mobile";
  if (width < TABLET_MAX) return "tablet";
  if (width < MEDIUM_MAX) return "medium";
  return "wide";
}

/**
 * True when the viewport is too narrow for the standard 4-pane desktop
 * grid — covers both the mobile shell (bottom bar, full-screen sheets)
 * and the tablet shell (rail visible, drawer side panel). Use this for
 * decisions that historically branched on `viewport === "compact"`.
 */
export function isCompactViewport(viewport: ViewportSize): boolean {
  return viewport === "mobile" || viewport === "tablet";
}

function readViewport(): ViewportSize {
  if (typeof window === "undefined") return "wide";
  return classifyViewport(window.innerWidth);
}

export function useViewportSize(): ViewportSize {
  const [size, setSize] = useState<ViewportSize>(readViewport);

  useEffect(() => {
    function handle() {
      setSize(classifyViewport(window.innerWidth));
    }
    window.addEventListener("resize", handle);
    return () => window.removeEventListener("resize", handle);
  }, []);

  return size;
}
